// @shared/store/useChatsStore.js
import { create } from "zustand";
import {
  ref,
  push,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  off,
  serverTimestamp,
  query,
  limitToLast,
  get,
  set,
} from "firebase/database";
import { rtdb } from "../../@shared/firebase/config";
import AudioManager from "../../@shared/utils/AudioManager";
import {
  getMessageSnippet,
  shouldShowMentionPreview,
} from "../utils/chatFeatures";

// Scope helpers
const paths = {
  party: (id) => ({
    meta: `partyChats/${id}/meta`,
    messages: `partyChats/${id}/messages`,
  }),
  lobby: (id) => ({
    meta: `lobbyChats/${id}/meta`,
    messages: `lobbyChats/${id}/messages`,
  }),
};

const tsToNumber = (ts) => {
  // RTDB serverTimestamp resolves to a number on read.
  // But guard for null/undefined/objects.
  if (typeof ts === "number") return ts;
  return 0;
};

const upsertIntoMap = (map, snap) => {
  const val = snap.val() || {};
  map.set(snap.key, { id: snap.key, ...val });
};

const toSortedArray = (map) => {
  return Array.from(map.values()).sort(
    (a, b) => tsToNumber(a.ts) - tsToNumber(b.ts)
  );
};

export const useChatsStore = create((set, get) => ({
  // state
  partyMessages: [], // last 50
  lobbyMessages: [], // last 50
  partyUnsub: null,
  lobbyUnsub: null,
  unread: { party: 0, lobby: 0 },
  drawerOpen: false,
  activeTab: "party", // "party" | "lobby"
  chatSoundEnabled: true,
  _lastPingAt: 0,
  mentionPreview: null,

  setChatSoundEnabled: (enabled) => set({ chatSoundEnabled: !!enabled }),

  _playChatPing: () => {
    const now = Date.now();
    const last = get()._lastPingAt || 0;
    if (!get().chatSoundEnabled) return;
    if (now - last < 800) return; // rate-limit
    set({ _lastPingAt: now });

    try {
      if (typeof AudioManager?.playSFX === "function") {
        AudioManager.playSFX("liveChat");
        return;
      }
    } catch {}

    try {
      AudioManager.playSFX("liveChat");
    } catch {}
  },

  setDrawerOpen: (v) => set({ drawerOpen: v }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  dismissMentionPreview: () => set({ mentionPreview: null }),

  // bootstrap meta if missing
  ensureMeta: async (scope, id, user) => {
    if (!id) return;

    const p = paths[scope](id);
    const metaRef = ref(rtdb, p.meta);
    const snap = await get(metaRef);
    const exists =
      typeof snap.exists === "function" ? snap.exists() : snap.val() !== null;

    if (!exists) {
      await set(metaRef, {
        createdAt: serverTimestamp(),
        createdBy: user?.id || "",
        createdByName: user?.username || "",
      });

      await push(ref(rtdb, p.messages), {
        text: `${user?.username || "Host"} created the party.`,
        sender: "system",
        senderId: "system",
        ts: serverTimestamp(),
      });
    }
  },

  // subscribe to last 50 (Party) — supports realtime updates (reactions, edits, etc.)
  subscribeParty: (partyId, selfId) => {
    if (!partyId) return;

    const p = paths.party(partyId);
    const messagesRef = query(ref(rtdb, p.messages), limitToLast(50));

    // Teardown any previous
    get().unsubscribeParty();

    // Use a map so updates don’t duplicate messages
    const messagesMap = new Map();

    const handleAdded = (snap) => {
      const val = snap.val() || {};
      upsertIntoMap(messagesMap, snap);
      set({ partyMessages: toSortedArray(messagesMap) });

      // unread if drawer closed or tab inactive and message not from self
      if (!get().drawerOpen || get().activeTab !== "party") {
        if (val.senderId && val.senderId !== selfId) {
          const u = get().unread;
          set({ unread: { ...u, party: (u.party || 0) + 1 } });
          if (val.sender !== "system") get()._playChatPing();
        }
      }

      if (
        shouldShowMentionPreview({
          message: val,
          selfId,
          chatOpen: get().drawerOpen && get().activeTab === "party",
        })
      ) {
        set({
          mentionPreview: {
            id: snap.key,
            scope: "party",
            sender: val.sender || "Player",
            snippet: getMessageSnippet(val, 90),
          },
        });
      }
    };

    const handleChanged = (snap) => {
      // 🔥 this is what makes reaction updates appear instantly
      upsertIntoMap(messagesMap, snap);
      set({ partyMessages: toSortedArray(messagesMap) });
    };

    const handleRemoved = (snap) => {
      messagesMap.delete(snap.key);
      set({ partyMessages: toSortedArray(messagesMap) });
    };

    // Attach listeners
    onChildAdded(messagesRef, handleAdded);
    onChildChanged(messagesRef, handleChanged);
    onChildRemoved(messagesRef, handleRemoved);

    // Store a single unsubscribe that detaches all listeners
    set({
      partyUnsub: () => {
        off(messagesRef, "child_added", handleAdded);
        off(messagesRef, "child_changed", handleChanged);
        off(messagesRef, "child_removed", handleRemoved);
      },
    });
  },

  // subscribe to last 50 (Lobby) — supports realtime updates (reactions, edits, etc.)
  subscribeLobby: (roomId, selfId) => {
    if (!roomId) return;

    const p = paths.lobby(roomId);
    const messagesRef = query(ref(rtdb, p.messages), limitToLast(50));

    // Teardown any previous
    get().unsubscribeLobby();

    const messagesMap = new Map();

    const handleAdded = (snap) => {
      const val = snap.val() || {};
      upsertIntoMap(messagesMap, snap);
      set({ lobbyMessages: toSortedArray(messagesMap) });

      if (!get().drawerOpen || get().activeTab !== "lobby") {
        if (val.senderId && val.senderId !== selfId) {
          const u = get().unread;
          set({ unread: { ...u, lobby: (u.lobby || 0) + 1 } });
          if (val.sender !== "system") get()._playChatPing();
        }
      }

      if (
        shouldShowMentionPreview({
          message: val,
          selfId,
          chatOpen: get().drawerOpen && get().activeTab === "lobby",
        })
      ) {
        set({
          mentionPreview: {
            id: snap.key,
            scope: "lobby",
            sender: val.sender || "Player",
            snippet: getMessageSnippet(val, 90),
          },
        });
      }
    };

    const handleChanged = (snap) => {
      upsertIntoMap(messagesMap, snap);
      set({ lobbyMessages: toSortedArray(messagesMap) });
    };

    const handleRemoved = (snap) => {
      messagesMap.delete(snap.key);
      set({ lobbyMessages: toSortedArray(messagesMap) });
    };

    onChildAdded(messagesRef, handleAdded);
    onChildChanged(messagesRef, handleChanged);
    onChildRemoved(messagesRef, handleRemoved);

    set({
      lobbyUnsub: () => {
        off(messagesRef, "child_added", handleAdded);
        off(messagesRef, "child_changed", handleChanged);
        off(messagesRef, "child_removed", handleRemoved);
      },
    });
  },

  markRead: (scope) => {
    const u = get().unread;
    set({ unread: { ...u, [scope]: 0 } });
  },

  unsubscribeParty: () => {
    const fn = get().partyUnsub;
    if (typeof fn === "function") fn();
    set({ partyUnsub: null, partyMessages: [] });
  },

  unsubscribeLobby: () => {
    const fn = get().lobbyUnsub;
    if (typeof fn === "function") fn();
    set({ lobbyUnsub: null, lobbyMessages: [] });
  },

  // Writer
  sendMessage: async ({ scope, id, text, user, payload = {} }) => {
    const isGiphy = payload?.type === "giphy";
    if (!isGiphy && !text?.trim()) return;
    if (!user?.emailVerified) {
      throw new Error(
        "Please verify your email in Account Settings to use Party and Lobby chat.",
      );
    }
    const p = paths[scope](id);
    await push(ref(rtdb, p.messages), {
      ...payload,
      text: isGiphy ? payload.text || "" : text.trim(),
      sender: user?.username || "unknown",
      senderId: user?.id || "",
      ts: serverTimestamp(),
    });
  },

  // teardown all
  cleanup: () => {
    get().unsubscribeParty();
    get().unsubscribeLobby();
  },
}));
